// frontend/src/App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider } from "./lib/useAuth";
import { CreditsProvider } from "./state/credits";
import { PriceProvider } from "./contexts/PriceContext";

import Layout from "./components/Layout";
import RequireAuth from "./components/RequireAuth";

import Landing from "./pages/Landing";
import Home from "./pages/Home";
import Create from "./pages/Create";
import Gallery from "./pages/Gallery";
import Pricing from "./pages/Pricing";
import Profile from "./pages/Profile";
import Result from "./pages/Result";
import CreatorHub from "./pages/CreatorHub";
import CreatorDirectory from "./pages/CreatorDirectory";
import ManageStyles from "./pages/ManageStyles"
import ContentPolicy from "./pages/ContentPolicy";
import GenerationsByStyle from "./pages/GenerationsByStyle";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import AdminPanel from "./pages/AdminPanel";

// ──────────────────────────────────────────────────────────────
// PAUSE SWITCH
// While true, the app shows a "paused" screen and NEVER initializes the
// Pi SDK or authenticates — so no sign-in, no session, no API calls.
// To reopen next quarter: set APP_PAUSED = false and redeploy. That's it.
// ──────────────────────────────────────────────────────────────
const APP_PAUSED = false;

export default function App() {
  const [piSdkReady, setPiSdkReady] = useState(false);

  useEffect(() => {
    if (APP_PAUSED) return; // skip all Pi SDK / auth work while paused
    try { localStorage.removeItem("mobile-app-webview-ui_access-token"); } catch {}
    initializePiSDK();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function initializePiSDK() {
    const MAX_WAIT = 8000;
    const POLL = 100;
    let waited = 0;

    while (waited < MAX_WAIT) {
      if (window.__PI_SDK_LOADED && window.Pi) break;        // our sdk.js loaded normally
      if (window.Pi && !window.__PI_SDK_LOADING) break;      // ✅ Pi Browser injected Pi natively
      if (!window.__PI_SDK_LOADING && !window.__PI_SDK_LOADED && waited > 2000) break; // truly not in Pi
      await new Promise(r => setTimeout(r, POLL));
      waited += POLL;
    }

    console.log("[Pi SDK] Wait complete:", {
      loaded: window.__PI_SDK_LOADED,
      hasPi: !!window.Pi,
      waited,
    });

    if (!window.Pi) {
      console.log("[Pi SDK] Not in Pi Browser - dev mode");
      setPiSdkReady(true);
      return;
    }

    try {
  // ✅ Pi.init() returns a Promise — MUST be awaited per official docs.
  // Always call init (idempotent); authenticate will throw if we skip.
  const isSandbox = window.__ENV?.sandbox === "true" || window.__ENV?.sandbox === true;
  console.log(`[Pi SDK] Calling Pi.init(sandbox=${isSandbox})...`);
  await window.Pi.init({ version: "2.0", sandbox: isSandbox });
  console.log(`[Pi SDK] ✓ Pi.init() resolved`);

  console.log("[Pi SDK] SDK ready, authenticating...");
  await authenticateWithPi();
  setPiSdkReady(true);
} catch (error) {
  console.error("[Pi SDK] ✗ Failed:", error);
  setPiSdkReady(true);
}
  }

  async function authenticateWithPi() {
    try {
      console.log("[Pi Auth] Authenticating...");

      const authResult = await Promise.race([
        window.Pi.authenticate(
          ["username", "payments", "wallet_address"],
          onIncompletePaymentFound
        ),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Pi auth timeout after 30s")), 30000)
        ),
      ]);

      if (!authResult || !authResult.accessToken) {
        console.warn("[Pi Auth] No access token received");
        return;
      }

      const user = authResult.user || {};
      const normalizedUser = {
        uid: user?.uid || user?.uid_v2 || null,
        username: user?.username ? `@${String(user.username).replace(/^@/, "")}` : null,
      };

      const authData = {
        accessToken: authResult.accessToken,
        user: normalizedUser,
        refreshedAt: Date.now(),
      };

      try {
        localStorage.setItem("artibytes_auth_v1", JSON.stringify(authData));
        sessionStorage.removeItem("ab_reauth_attempted");
        console.log("[Pi Auth] ✓ Authenticated:", normalizedUser.username);
      } catch (e) {
        console.warn("[Pi Auth] Failed to save to localStorage:", e);
      }

      // Handle any incomplete payments after auth succeeds
      handleIncompletePayments(authResult.accessToken);

    } catch (error) {
      console.error("[Pi Auth] Authentication failed or timed out:", error.message);
    }
  }

  // SDK callback: fired when Pi finds an incomplete payment during auth/createPayment.
  // A real handler (not a no-op) lets us resolve the payment inline so the SDK
  // doesn't keep showing the blocking "Pending Payment Found" dialog.
  // We only COMPLETE payments that already have a txid (they're ready to finish).
  // We never re-approve here — re-approving an already-approved payment is what
  // caused the /approve 502 storm.
  function onIncompletePaymentFound(payment) {
    try {
      const paymentId = payment?.identifier;
      const txid = payment?.transaction?.txid;
      console.log("[Pi Auth] onIncompletePaymentFound:", { paymentId, hasTxid: !!txid });
      if (!paymentId || !txid) {
        // No txid → not completable; leave it for the backend reconcile/cancel path.
        return;
      }
      const token = (() => {
        try {
          const raw = localStorage.getItem("artibytes_auth_v1");
          return raw ? JSON.parse(raw)?.accessToken : null;
        } catch { return null; }
      })();
      if (!token) return;
      resolveIncompletePayment(paymentId, txid, token);
    } catch (e) {
      console.error("[Pi Auth] onIncompletePaymentFound error:", e);
    }
  }

  function apiBase() {
    return process.env.REACT_APP_API_BASE ||
      (window.__ARTIBYTES_API_BASE__ ? String(window.__ARTIBYTES_API_BASE__) : "") ||
      "https://artibytes-api-191042099955.europe-west4.run.app";
  }

  // Complete a single incomplete payment that has a txid. Never approves.
  function resolveIncompletePayment(paymentId, txid, token) {
    const headers = {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
      "x-pi-access-token": token,
    };
    return fetch(`${apiBase()}/api/payments/complete`, {
      method: "POST",
      credentials: "omit",
      headers,
      body: JSON.stringify({ paymentId, txId: txid }),
    })
      .then(() => console.log("[Pi Auth] Resolved incomplete payment:", paymentId))
      .catch((e) => console.error("[Pi Auth] Failed to resolve:", paymentId, e));
  }

  function handleIncompletePayments(token) {
    const headers = {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
      "x-pi-access-token": token,
    };

    fetch(`${apiBase()}/api/payments/incomplete`, {
      method: "GET",
      credentials: "omit",
      headers,
    })
      .then(r => r.json())
      .then(data => {
        const payments = data?.payments || [];
        console.log("[Pi Auth] Incomplete payments to resolve:", payments.length);
        payments.forEach((payment) => {
          const paymentId = payment?.identifier;
          const txid = payment?.transaction?.txid;
          if (!paymentId) return;
          // Only complete payments that have a txid. Do NOT re-approve no-txid
          // payments — that re-approve loop caused the /approve 502 storm.
          if (txid) {
            resolveIncompletePayment(paymentId, txid, token);
          } else {
            console.log("[Pi Auth] Skipping no-txid incomplete (left for reconcile/cancel):", paymentId);
          }
        });
      })
      .catch(() => {
        console.log("[Pi Auth] No incomplete payments or endpoint not found");
      });
  }

  // ── Paused screen: shown to everyone, blocks sign-in entirely ──
  if (APP_PAUSED) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: "24px",
          fontFamily: "system-ui, sans-serif",
          background: "#0f0f17",
          color: "#fff",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 420 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏸️</div>
          <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 12 }}>
            ArtiBytes is paused
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: "#b8b8c8" }}>
            We're temporarily paused while we wait on Pi mainnet wallet approval.
            Thanks for your interest — please check back next quarter.
          </p>
        </div>
      </div>
    );
  }

  if (!piSdkReady) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "50px",
              height: "50px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #6D5EF9",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p style={{ color: "#666" }}>Loading Pi SDK...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <PriceProvider>
      <AuthProvider>
        <CreditsProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Navigate to="/landing" replace />} />
              <Route path="/landing" element={<Landing />} />

              <Route element={<Layout />}>
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/content-policy" element={<ContentPolicy />} />

                <Route path="/home" element={<RequireAuth><Home /></RequireAuth>} />
                <Route path="/create" element={<RequireAuth><Create /></RequireAuth>} />
                <Route path="/gallery" element={<RequireAuth><Gallery /></RequireAuth>} />
                <Route path="/creator-hub" element={<CreatorHub />} />
                <Route path="/pricing" element={<RequireAuth><Pricing /></RequireAuth>} />
                <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
                <Route path="/creator" element={<RequireAuth><CreatorHub /></RequireAuth>} />
                <Route path="/creator-directory" element={<RequireAuth><CreatorDirectory /></RequireAuth>} />
                <Route path="/admin" element={<AdminPanel />} />
                <Route path="/manage-styles" element={<RequireAuth><ManageStyles /></RequireAuth>} />
                <Route
                  path="/creator/styles/:styleId"
                  element={<RequireAuth><GenerationsByStyle /></RequireAuth>}
                />
                <Route
                  path="/generations-by-style/:styleId"
                  element={<RequireAuth><GenerationsByStyle /></RequireAuth>}
                />
                <Route path="/result/:jobId" element={<RequireAuth><Result /></RequireAuth>} />
                <Route path="/result" element={<Navigate to="/gallery" replace />} />
              </Route>

              <Route path="*" element={<Navigate to="/landing" replace />} />
            </Routes>
          </BrowserRouter>
        </CreditsProvider>
      </AuthProvider>
    </PriceProvider>
  );
}